import type {
  HotspotPosition,
  Placement,
  Storage as TourKitStorage,
} from '@tour-kit/core';
import {
  HintTooltip,
  HintsProvider,
  useHints,
  type HintConfig,
} from '@tour-kit/hints';
import { HintHeadless } from '@tour-kit/hints/headless';
import { X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import {
  HINT_ID,
  guidanceTargetSelector,
  type HintId,
} from '@/core/guidance/constants';
import { trackGuidanceEvent } from '@/core/guidance/guidance-analytics';
import { useGuidanceStore } from '@/core/guidance/guidance.store';
import {
  HINT_CATALOG,
  onProductsList,
  pickActiveHint,
  type HintEligibilityContext,
} from '@/core/guidance/hints-eligibility';
import { useGetStartedDomain } from '@/core/guidance/use-get-started-domain';
import {
  computeHintBeaconPosition,
  HINT_BEACON_DEFAULT_SIZE,
} from '@/core/guidance/clamp-hint-anchor-rect';
import { useLiveTargetRect } from '@/core/guidance/use-live-target-rect';
import { useDictionary } from '@/core/providers/dictionary-provider';

import '@tour-kit/hints/styles/variables.css';

type GuidanceHintsProviderProps = {
  children: ReactNode;
  storage: TourKitStorage;
};

type HintCopy = {
  title: string;
  body: string;
};

function hintCopyMap(
  dict: ReturnType<typeof useDictionary>,
): Record<HintId, HintCopy> {
  const h = dict.guidance.hints;
  return {
    [HINT_ID.navProducts]: h['hint.nav-products'],
    [HINT_ID.profilesEmpty]: h['hint.profiles-empty'],
    [HINT_ID.profileLinkCard]: h['hint.profile-link-card'],
    [HINT_ID.shareProfile]: h['hint.share-profile'],
    [HINT_ID.productsEmpty]: h['hint.products-empty'],
    [HINT_ID.liveScan]: h['hint.live-scan'],
    [HINT_ID.locationsEmpty]: h['hint.locations-empty'],
    [HINT_ID.leadsInbox]: h['hint.leads-inbox'],
    [HINT_ID.peopleInvite]: h['hint.people-invite'],
    [HINT_ID.reputationSetup]: h['hint.reputation-setup'],
  };
}

/** HintsProvider types `storage` as DOM Storage; TourKit only needs get/set/remove. */
function asHintsStorage(storage: TourKitStorage): globalThis.Storage {
  return {
    get length() {
      return 0;
    },
    clear() {},
    key() {
      return null;
    },
    getItem: (key) => {
      const value = storage.getItem(key);
      return typeof value === 'string' || value === null ? value : null;
    },
    setItem: (key, value) => {
      storage.setItem(key, value);
    },
    removeItem: (key) => {
      storage.removeItem(key);
    },
  };
}

/** Controls up to this many chip areas count as covered (buttons, selects). */
const SMALL_CONTROL_AREA_FACTOR = 10;

const INTERACTIVE_SELECTOR =
  'button, a[href], input, select, textarea, [role="button"], [role="combobox"], [role="switch"], [role="checkbox"], [role="tab"]';

/**
 * Would a chip at `rect` sit on top of a control other than the hint target?
 * Samples the corners and the centre (DEV-579: sort select, copy button).
 * Large tappable surfaces (whole product cards) do not count: resting on a
 * card edge is fine, hiding a button or a select is not.
 */
function coversOtherControl(
  rect: DOMRect,
  target: HTMLElement,
  beacon: HTMLElement | null,
): boolean {
  const inset = 2;
  const points: [number, number][] = [
    [rect.left + inset, rect.top + inset],
    [rect.right - inset, rect.top + inset],
    [rect.left + inset, rect.bottom - inset],
    [rect.right - inset, rect.bottom - inset],
    [rect.left + rect.width / 2, rect.top + rect.height / 2],
  ];
  return points.some(([x, y]) => {
    const hit = document
      .elementsFromPoint(x, y)
      .find(
        (el) => !beacon?.contains(el) && !el.closest('.guidance-hint-tooltip'),
      );
    const control = hit?.closest(INTERACTIVE_SELECTOR);
    if (!control || control.contains(target)) return false;
    const box = control.getBoundingClientRect();
    return (
      box.width * box.height <
      rect.width * rect.height * SMALL_CONTROL_AREA_FACTOR
    );
  });
}

type GuidanceHintBeaconProps = {
  targetElement: HTMLElement;
  /** TourKit's last rect — used until the Ionic-aware tracker measures. */
  fallbackRect: DOMRect;
  hotspotRef: RefObject<HTMLButtonElement | null>;
  position: HotspotPosition;
  tooltipPlacement: Placement;
  isOpen: boolean;
  beaconLabel: string;
  beaconAria: string;
  title: string;
  body: string;
  dismissLabel: string;
  onToggle: () => void;
  onHide: () => void;
  onDismiss: () => void;
};

/**
 * Portal + live rect so the chip stays on the target while ion-content scrolls.
 * (TourKit headless mounts in-tree; Ionic shadow scroll + transform ancestors
 * otherwise leave the tip stuck or scrolling away from the button.)
 *
 * Position is owned here (not CSS translate on TourKit's corner anchor) so the
 * full chip stays inside the iOS safe area — DEV-361 kept regressing otherwise.
 */
function GuidanceHintBeacon({
  targetElement,
  fallbackRect,
  hotspotRef,
  position: _position,
  tooltipPlacement,
  isOpen,
  beaconLabel,
  beaconAria,
  title,
  body,
  dismissLabel,
  onToggle,
  onHide,
  onDismiss,
}: GuidanceHintBeaconProps) {
  const [hotspotElement, setHotspotElement] =
    useState<HTMLButtonElement | null>(null);
  const beaconSizeRef = useRef(HINT_BEACON_DEFAULT_SIZE);

  const applyBeaconPosition = useCallback(
    (rawRect: DOMRect) => {
      const node = hotspotRef.current;
      if (node) {
        const measured = node.getBoundingClientRect();
        if (measured.width >= 8 && measured.height >= 8) {
          beaconSizeRef.current = {
            width: measured.width,
            height: measured.height,
          };
        }
      }
      if (!node) return;
      const next = computeHintBeaconPosition(
        rawRect,
        beaconSizeRef.current,
        (candidate) => coversOtherControl(candidate, targetElement, node),
      );
      if (!next) {
        node.style.visibility = 'hidden';
        node.style.pointerEvents = 'none';
        return;
      }
      node.style.visibility = 'visible';
      node.style.pointerEvents = '';
      node.style.top = `${next.top}px`;
      node.style.left = `${next.left}px`;
      node.style.transform = 'none';
    },
    [hotspotRef, targetElement],
  );

  // Escape or a tap elsewhere closes the tip (DEV-579); the tap still reaches
  // whatever was tapped.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onHide();
    };
    const onPointerDown = (event: PointerEvent) => {
      const el = event.target;
      if (!(el instanceof Element)) return;
      if (hotspotRef.current?.contains(el)) return;
      if (el.closest('.guidance-hint-tooltip')) return;
      onHide();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [isOpen, onHide, hotspotRef]);

  const mergedHotspotRef = useCallback(
    (node: HTMLButtonElement | null) => {
      hotspotRef.current = node;
      setHotspotElement(node);
      if (node && targetElement.isConnected) {
        applyBeaconPosition(targetElement.getBoundingClientRect());
      }
    },
    [hotspotRef, applyBeaconPosition, targetElement],
  );

  useLiveTargetRect(targetElement, applyBeaconPosition);

  const initialPos = computeHintBeaconPosition(
    fallbackRect,
    HINT_BEACON_DEFAULT_SIZE,
  ) ?? {
    top: fallbackRect.top + 4,
    left: Math.max(8, fallbackRect.right - 4 - HINT_BEACON_DEFAULT_SIZE.width),
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <>
      <button
        ref={mergedHotspotRef}
        type="button"
        className="guidance-hint-beacon"
        style={{
          position: 'fixed',
          top: initialPos.top,
          left: initialPos.left,
          transform: 'none',
        }}
        aria-label={beaconAria}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={onToggle}
      >
        <span className="guidance-hint-beacon__dot" aria-hidden />
        <span className="guidance-hint-beacon__label">{beaconLabel}</span>
      </button>
      {isOpen && hotspotElement ? (
        <HintTooltip
          target={hotspotElement}
          placement={tooltipPlacement}
          onClose={onHide}
          title={title}
          zIndex="high"
          className="guidance-hint-tooltip"
          closeButton={
            <button
              type="button"
              className="guidance-hint-tooltip-close"
              aria-label={dismissLabel}
              onClick={onDismiss}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          }
        >
          <p className="guidance-hint-tooltip-body">{body}</p>
        </HintTooltip>
      ) : null}
    </>,
    document.body,
  );
}

/** Renders at most one eligible hint (suppressed while a tour is active). */
export function GuidanceHintsActive() {
  const dict = useDictionary();
  const copy = useMemo(() => hintCopyMap(dict), [dict]);
  const location = useLocation();
  const domain = useGetStartedDomain();
  const isTourActive = useGuidanceStore((s) => s.isTourActive);
  const checklistHintGate = useGuidanceStore((s) => s.checklistHintGate);
  const dismissedHintIds = useGuidanceStore((s) => s.dismissedHintIds);
  const markHintDismissed = useGuidanceStore((s) => s.markHintDismissed);
  const { hints, dismissHint } = useHints();

  const dismissed = useMemo(() => {
    const set = new Set<HintId>();
    for (const id of dismissedHintIds) {
      set.add(id as HintId);
    }
    for (const hint of hints) {
      if (hint.isDismissed) set.add(hint.id as HintId);
    }
    return set;
  }, [hints, dismissedHintIds]);

  // Keep session map in sync with TourKit (survives HintHeadless unmount/unregister).
  useEffect(() => {
    for (const hint of hints) {
      if (hint.isDismissed) markHintDismissed(hint.id);
    }
  }, [hints, markHintDismissed]);

  // nav-products = “find Products” — visiting the list completes discovery.
  useEffect(() => {
    if (!onProductsList(location.pathname)) return;
    if (dismissed.has(HINT_ID.navProducts)) return;
    markHintDismissed(HINT_ID.navProducts);
    dismissHint(HINT_ID.navProducts);
    trackGuidanceEvent({
      name: 'hint_dismissed',
      metadata: { hintId: HINT_ID.navProducts, reason: 'visited' },
    });
  }, [location.pathname, dismissed, dismissHint, markHintDismissed]);

  const ctx: HintEligibilityContext = {
    domain,
    pathname: location.pathname,
    isTourActive,
    shareDone: checklistHintGate.shareDone,
    dismissed,
  };

  const active = pickActiveHint(ctx);
  const shownRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active) {
      shownRef.current = null;
      return;
    }
    if (shownRef.current === active.id) return;
    shownRef.current = active.id;
    trackGuidanceEvent({
      name: 'hint_shown',
      metadata: { hintId: active.id },
    });
  }, [active]);

  if (!active) return null;

  const labels = copy[active.id];
  const hintsCopy = dict.guidance.hints;
  const beaconLabel = hintsCopy.beaconLabel;
  const beaconAria = `${beaconLabel}: ${labels.title}`;

  return (
    <HintHeadless
      key={active.id}
      id={active.id}
      target={guidanceTargetSelector(active.target)}
      content={labels.body}
      persist
      pulse
      autoShow={false}
      position="top-right"
      tooltipPlacement="bottom"
      onDismiss={() => {
        markHintDismissed(active.id);
        trackGuidanceEvent({
          name: 'hint_dismissed',
          metadata: { hintId: active.id },
        });
      }}
      render={({
        isOpen,
        show,
        hide,
        dismiss,
        targetElement,
        targetRect,
        hotspotRef,
        position,
        tooltipPlacement,
      }) => {
        // Missing / detached / zero-size targets otherwise pin to 0,0 (top-left).
        if (
          !targetElement?.isConnected ||
          !targetRect ||
          targetRect.width < 8 ||
          targetRect.height < 8
        ) {
          return null;
        }

        return (
          <GuidanceHintBeacon
            targetElement={targetElement}
            fallbackRect={targetRect}
            hotspotRef={hotspotRef}
            position={position ?? 'top-right'}
            tooltipPlacement={tooltipPlacement ?? 'bottom'}
            isOpen={isOpen}
            beaconLabel={beaconLabel}
            beaconAria={beaconAria}
            title={labels.title}
            body={labels.body}
            dismissLabel={hintsCopy.dismiss}
            onToggle={() => {
              if (isOpen) hide();
              else show();
            }}
            onHide={hide}
            onDismiss={() => {
              markHintDismissed(active.id);
              dismiss();
            }}
          />
        );
      }}
    />
  );
}

export function GuidanceHintsProvider({
  children,
  storage,
}: GuidanceHintsProviderProps) {
  const dict = useDictionary();
  const copy = useMemo(() => hintCopyMap(dict), [dict]);

  const hintConfigs = useMemo<HintConfig[]>(
    () =>
      HINT_CATALOG.map((hint) => ({
        id: hint.id,
        target: guidanceTargetSelector(hint.target),
        title: copy[hint.id].title,
        content: copy[hint.id].body,
        // Permanent dismiss only via X → dismiss(); reopen after hide() must work.
        // Do not use frequency: 'once' — it blocks show() after the first open.
        persist: true,
        pulse: true,
        autoShow: false,
        position: 'top-right',
        tooltipPlacement: 'bottom',
      })),
    [copy],
  );

  return (
    <HintsProvider hints={hintConfigs} storage={asHintsStorage(storage)}>
      {children}
      <GuidanceHintsActive />
    </HintsProvider>
  );
}
